import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useBilling } from "@/contexts/BillingContext";
import { BILLING } from "@/constants/testIds";
import { CheckCircle2, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { trackPurchase } from "@/lib/analytics";

// 30 attempts × 2s = 60s of polling. Generous to accommodate 3D Secure flows.
const MAX_ATTEMPTS = 30;
const POLL_MS = 2000;

export default function BillingSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useBilling();
  const [state, setState] = useState("checking"); // checking | paid | timeout | expired | error
  const [info, setInfo] = useState(null);
  const stoppedRef = useRef(false);
  const attemptsRef = useRef(0);
  const purchaseTrackedRef = useRef(false);

  // Fire GA4 "purchase" event exactly once when payment confirms.
  useEffect(() => {
    if (state === "paid" && !purchaseTrackedRef.current) {
      purchaseTrackedRef.current = true;
      trackPurchase(sessionId);
    }
  }, [state, sessionId]);

  const checkPro = useCallback(async () => {
    // Direct read of user state — succeeds even if webhook arrived faster than polling.
    try {
      const { data } = await api.get("/billing/me");
      if (data?.is_pro) {
        stoppedRef.current = true;
        setState("paid");
        refresh();
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }, [refresh]);

  const startPolling = useCallback(() => {
    stoppedRef.current = false;
    attemptsRef.current = 0;
    setState("checking");
    let timer = null;

    const poll = async () => {
      if (stoppedRef.current) return;
      attemptsRef.current += 1;
      // First: check if user is already Pro (webhook may have arrived).
      if (await checkPro()) return;
      // Then: poll the Stripe session if we have one.
      if (sessionId) {
        try {
          const { data } = await api.get(`/billing/status/${sessionId}`);
          setInfo(data);
          if (data.payment_status === "paid" || data.is_pro) {
            stoppedRef.current = true;
            setState("paid");
            refresh();
            return;
          }
          if (data.status === "expired") {
            stoppedRef.current = true;
            setState("expired");
            return;
          }
        } catch {
          // soft-fail — webhook may still complete out of band
        }
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        stoppedRef.current = true;
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      stoppedRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, refresh, checkPro]);

  useEffect(() => {
    const cleanup = startPolling();
    return cleanup;
  }, [startPolling]);

  const retryCheck = async () => {
    // Quick manual recheck — first try direct Pro state, then a fresh poll cycle.
    if (await checkPro()) return;
    startPolling();
  };

  return (
    <div
      data-testid={BILLING.successScreen}
      className="border-t border-border min-h-[70vh] flex items-center justify-center px-6 py-16"
    >
      <div className="max-w-xl w-full bg-card border border-border p-10 lg:p-14 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#002FA7] animate-spin mx-auto mb-6" />
            <div className="sux-marker mb-3" data-testid={BILLING.successStatus}>
              {t("billing.success_pending")}
            </div>
            <h1 className="text-3xl font-black tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
              {t("billing.success_pending_title")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("billing.success_pending_body")}
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#002FA7] mx-auto mb-6" />
            <div className="sux-marker mb-3" data-testid={BILLING.successStatus}>
              {t("billing.success_marker")}
            </div>
            <h1 className="text-4xl font-black tracking-tight mb-4 leading-tight" style={{ fontFamily: "Outfit" }}>
              {t("billing.success_title", { name: "" }).trim()}
            </h1>
            <p className="text-muted-foreground text-sm mb-8">
              {t("billing.success_body")}
              {info?.amount_total ? (
                <>
                  {" "}
                  <span className="font-mono">
                    {(info.amount_total / 100).toFixed(2)} {String(info.currency).toUpperCase()}
                  </span>
                </>
              ) : null}
            </p>
            <button
              onClick={() => navigate("/")}
              data-testid={BILLING.successContinue}
              className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-[11px] py-4 hover:bg-[#00227A] transition-colors"
            >
              {t("billing.success_cta")}
            </button>
          </>
        )}

        {state === "expired" && (
          <>
            <AlertTriangle className="w-12 h-12 text-[#FF2A00] mx-auto mb-6" />
            <h1 className="text-3xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
              {t("billing.success_expired_title")}
            </h1>
            <p className="text-muted-foreground text-sm mb-8">
              {t("billing.success_expired_body")}
            </p>
            <Link
              to="/billing/upgrade"
              className="inline-block w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-[11px] py-4 hover:bg-[#001E6B] transition-colors"
            >
              {t("billing.success_expired_cta")}
            </Link>
          </>
        )}

        {(state === "timeout" || state === "error") && (
          <>
            <AlertTriangle className="w-12 h-12 text-[#FF2A00] mx-auto mb-6" />
            <div className="sux-marker mb-3" data-testid={BILLING.successStatus}>
              {state === "error" ? t("billing.success_error_title") : t("billing.success_pending")}
            </div>
            <h1 className="text-3xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
              {state === "error" ? t("billing.success_error_title") : t("billing.success_pending_title")}
            </h1>
            <p className="text-muted-foreground text-sm mb-8">
              {t("billing.success_error_body")}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={retryCheck}
                className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-[11px] py-4 hover:bg-[#00227A] transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-3 h-3" /> {t("common.try_again")}
              </button>
              <Link
                to="/"
                className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-[11px] py-4 hover:bg-[#001E6B] transition-colors inline-block"
              >
                {t("billing.success_error_cta")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
